import { useMemo } from 'react';
import {
  GetNotificationChannelsQuery,
  GetNotificationChannelsQueryVariables,
  NotificationChannel,
  NotificationChannelsOrderByInput,
  SortOrder,
  useGetNotificationChannelsQuery,
} from '../../gql/generated';
import {
  ChannelFilters,
  useChannelFilters,
} from '../../hooks/useChannelFilters';
import { useTableState } from '../../hooks/useTableState';

export function useChannelsTable() {
  const { filters, graphQLFilter } = useChannelFilters();

  const tableState = useTableState<
    NotificationChannel,
    ChannelFilters,
    GetNotificationChannelsQuery,
    GetNotificationChannelsQueryVariables
  >({
    useQuery: useGetNotificationChannelsQuery,
    dataAccessor: (data) => data?.notificationChannels.rows ?? [],
    graphQLFilter,
    externalFilters: filters,
    buildOrderBy: (sorting): NotificationChannelsOrderByInput => {
      if (sorting.length === 0) return {};

      const firstSort = sorting[0];

      // Map column IDs to GraphQL field names
      const columnIdToFieldMap: Record<string, string> = {
        channelName: 'name',
        type: 'channelType',
      };

      const fieldName = columnIdToFieldMap[firstSort.id] || firstSort.id;

      return {
        [fieldName]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
      } as NotificationChannelsOrderByInput;
    },
    localStoragePrefix: 'channels',
    defaultPageSize: 20,
  });

  // Calculate total count and pages from GraphQL response
  const totalCount = useMemo(() => {
    return tableState.rawData?.notificationChannels?.count ?? 0;
  }, [tableState.rawData]);

  const totalPages = useMemo(() => {
    return totalCount > 0
      ? Math.ceil(totalCount / tableState.pagination.pageSize)
      : 0;
  }, [totalCount, tableState.pagination.pageSize]);

  return {
    ...tableState,
    // Data
    channels: tableState.data,
    refetch: tableState.refetch,
    totalPages,
    totalCount,
    filters,

    // Pagination
    pageIndex: tableState.pagination.pageIndex,
    pageSize: tableState.pagination.pageSize,
    setPageIndex: (pageIndex: number) => {
      tableState.onPaginationChange({
        pageIndex,
        pageSize: tableState.pagination.pageSize,
      });
    },
    setPageSize: (pageSize: number) => {
      tableState.onPaginationChange({ pageIndex: 0, pageSize });
    },

    // Sorting
    setSorting: tableState.onSortingChange,

    // Column settings
    setColumnPinning: tableState.onColumnPinningChange,
    setColumnVisibility: tableState.onColumnVisibilityChange,
    setColumnOrder: tableState.onColumnOrderChange,
  };
}
